//// Application Packages
import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { object, string } from 'yup'
import { translateField } from '../../functions/field-functions'
import {
    subtractValuesAdditionalConditionalForm,
    extractSubmissionForm,
} from '../../functions/form-functions'

//// Application Slices
import { selectSettings } from '../../reducers/settingSlice'
import {
    updateFormData,
    updateFormValidity,
    updateFormAuxiliary,
    deleteFormData,
    updateFormSystem,
    selectForms,
} from '../../reducers/formSlice'

//// Application Components
import ButtonComponent from '../../components/conditional-components/button-component'
import LoadingComponent from '../../components/simple-components/loading-component'
import NotificationComponent from '../../components/conditional-components/notification-component'
import AdditionalMilitaryWaiverDateForm from '../../components/generic-components/additional-military-waiver-date-component'
import AdditionalMilitaryWaiverNameForm from '../../components/generic-components/additional-military-waiver-name-component'

//// Carbon Components
import { Form, RadioButtonGroup, RadioButton } from '@carbon/react'

//// Carbon Icons

//// Static Constants

//////// Component
const MilitaryFeeWaiverForm = () => {
    //////// Hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()

    //////// State
    const [loading, setLoading] = useState(false)
    const [notification, setNotification] = useState({})

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const formsData = useSelector(selectForms)

    //// Slice Data
    const digital_name = settingsData?.['form']?.['digital_name']
    const submit_route = settingsData?.['form']?.['submit_route']
    const fields = settingsData?.['form']?.['fields']
    const conditions = settingsData?.['form']?.['conditions']

    //////// Search Params

    //////// Methods

    // Validate Data Submission
    const validation = async (submit) => {
        // Validate Form Submission Data Against Schema
        try {
            // Schema Object
            const schema = {}
            if (
                formsData?.[digital_name]?.values?.HON_DISCHG === undefined ||
                formsData?.[digital_name]?.values?.HON_DISCHG === 'N'
            ) {
                schema.yup = object({
                    HON_DISCHG: string().required(
                        'This field is required to be entered or selected.'
                    ),
                })
                schema.valid = {
                    HON_DISCHG: false,
                    HON_DISCHGErrMsg: '',
                }
            } else if (
                formsData?.[digital_name]?.values?.HON_DISCHG === 'Y' &&
                formsData?.[digital_name]?.values?.ACTV === 'A'
            ) {
                schema.yup = object({
                    HON_DISCHG: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    ACTV: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    DUTY_END: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    VET_NAME: string().required(
                        'This field is required to be entered or selected.'
                    ),
                })
                schema.valid = {
                    HON_DISCHG: false,
                    HON_DISCHGErrMsg: '',
                    ACTV: false,
                    ACTVErrMsg: '',
                    DUTY_END: false,
                    DUTY_ENDErrMsg: '',
                    VET_NAME: false,
                    VET_NAMEErrMsg: '',
                }
            } else if (
                formsData?.[digital_name]?.values?.HON_DISCHG === 'Y' &&
                formsData?.[digital_name]?.values?.ACTV === 'S'
            ) {
                schema.yup = object({
                    HON_DISCHG: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    ACTV: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    DUTY_END: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    SPOUSE_NAME: string().required(
                        'This field is required to be entered or selected.'
                    ),
                })
                schema.valid = {
                    HON_DISCHG: false,
                    HON_DISCHGErrMsg: '',
                    ACTV: false,
                    ACTVErrMsg: '',
                    DUTY_END: false,
                    DUTY_ENDErrMsg: '',
                    SPOUSE_NAME: false,
                    SPOUSE_NAMEErrMsg: '',
                }
            } else if (formsData?.[digital_name]?.values?.HON_DISCHG === 'Y') {
                schema.yup = object({
                    HON_DISCHG: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    ACTV: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    DUTY_END: string().required(
                        'This field is required to be entered or selected.'
                    ),
                })
                schema.valid = {
                    HON_DISCHG: false,
                    HON_DISCHGErrMsg: '',
                    ACTV: false,
                    ACTVErrMsg: '',
                    DUTY_END: false,
                    DUTY_ENDErrMsg: '',
                }
            }

            await schema.yup.validate(
                { ...submit },
                { strict: true, abortEarly: false }
            )
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: {},
                })
            )
            return true
        } catch (err) {
            const errors = {}
            if (err?.inner?.length > 0) {
                await err.inner.forEach(async (el) => {
                    if (el?.path) {
                        errors[el?.path] = true
                        if (el?.errors?.length > 0) {
                            const errName = el?.path + 'ErrMsg'
                            const errMsg = el?.errors?.join(' ')
                            const tran = translateField(fields, el?.path)
                            if (tran) {
                                const tranErrMsg = await errMsg.replaceAll(
                                    el?.path,
                                    tran
                                )
                                errors[errName] = tranErrMsg
                            } else {
                                errors[errName] = el?.errors?.join(' ')
                            }
                        }
                    }
                })
            }
            //// Start Watson Assistant Journey

            //// End Watson Assistant Journey
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: errors,
                })
            )
            return false
        }
    }

    // Re-Validate Data Submission
    const revalidation = async (event_name) => {
        try {
            // Update Validation State
            if (digital_name && event_name) {
                dispatch(
                    updateFormValidity({
                        digital_name: digital_name,
                        errors: {
                            [event_name]: false,
                            [event_name + 'ErrMsg']: '',
                        },
                    })
                )
            }
        } catch (error) {
            console.warn('Revalidation Error:', error)
            return null
        }
    }

    //////// Functional Methods

    // Handle Removal Of Additional Examination History Component/s
    const handleSubtractAdditionalConditionalComponent = (e) => {
        try {
            if (e?.target?.name && e?.target?.value) {
                const values = subtractValuesAdditionalConditionalForm(
                    conditions,
                    formsData?.[digital_name]?.values,
                    e?.target?.name,
                    e?.target?.value
                )
                if (values) {
                    dispatch(
                        deleteFormData({
                            digital_name: digital_name,
                        })
                    )
                    dispatch(
                        updateFormAuxiliary({
                            digital_name: digital_name,
                            auxiliary: {
                                addtForms: [],
                            },
                        })
                    )
                    dispatch(
                        updateFormData({
                            digital_name: digital_name,
                            values: values,
                        })
                    )
                }
            }
        } catch (error) {
            console.warn(
                'Handle Subtract Additional Examination History Component Error:',
                error
            )
            return null
        }
    }

    // Handle Data Of All Fields
    const handleData = (e) => {
        if (e?.target?.name && e?.target?.value) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: {
                        [e.target.name]: e.target.value,
                    },
                })
            )
            revalidation(e.target.name)
        } else if (e?.target?.name) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: { [e.target.name]: '' },
                })
            )
            revalidation(e.target.name)
        }
    }

    // Handle Form Submission
    const handleSubmit = async () => {
        try {
            console.info('Military Fee Waiver: Submitted')

            setLoading(true)

            // Extract Form Data From Redux
            const submit = await extractSubmissionForm(digital_name, formsData)

            // Validate Form Submission Data
            const valid = await validation(submit)

            // Check If Form Data Is Valid
            if (valid) {
                console.info('Military Fee Waiver: Passed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: true },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: false,
                    kind: '',
                    title: '',
                    subtitle: '',
                })

                // Navigate To Next Form
                navigate(submit_route ? submit_route : '/')
            } else {
                console.info('Military Fee Waiver: Failed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: false },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: true,
                    kind: 'error',
                    title: 'Form Errors:',
                    subtitle:
                        'There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.',
                })

                console.log(
                    'Military Fee Waiver Failed Valid: There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.'
                )
            }
        } catch (error) {
            console.warn('Handle Submit Error:', error)
            return null
        }
    }

    // Handle Keyboard Enter As Submit (Method)
    const handleKeyDown = async (event) => {
        try {
            if (event?.key === 'Enter') {
                await handleSubmit()
            }
        } catch (error) {
            console.warn('Handle Down Key Error:', error)
            return null
        }
    }

    //////// Memoization

    //////// Effects/Side Effects

    //////////////// Render & Return
    if (loading) {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <LoadingComponent />
                </Form>
            </>
        )
    } else {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <p>
                        If you are a military veteran or the spouse of a
                        military veteran, you may be eligible for a waiver of
                        the initial application and licensure fees. Your
                        application must be submitted within 60 months after the
                        eligible veteran&apos;s honorable discharge from any
                        branch of the United States Armed Forces.
                    </p>

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.HON_DISCHG
                                ? 'required invalid'
                                : 'required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.HON_DISCHG
                        }
                        valueSelected={
                            formsData?.[digital_name]?.values?.HON_DISCHG
                        }
                        legendText="Was the qualifying veteran honorably discharged within 60 months of the application?"
                        name="HON_DISCHG"
                        invalid={formsData?.[digital_name]?.errors?.HON_DISCHG}
                        invalidText={
                            formsData?.[digital_name]?.errors?.HON_DISCHGErrMsg
                        }
                    >
                        <RadioButton
                            id="HON_DISCHGYes"
                            name="HON_DISCHGYes"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                        <RadioButton
                            id="HON_DISCHGNo"
                            name="HON_DISCHGNo"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                    </RadioButtonGroup>

                    <AdditionalMilitaryWaiverDateForm />

                    <AdditionalMilitaryWaiverNameForm />

                    <NotificationComponent notification={notification} />

                    <div className="button-group">
                        <div className="button-group-item">
                            <ButtonComponent
                                click={handleSubmit}
                                title="Save Progress"
                            />
                        </div>
                    </div>
                </Form>
            </>
        )
    }
}

export default MilitaryFeeWaiverForm
